import { render, statusBar, cardDisplay } from './components';
import { showCardDetail } from './CardDetailModal';
import { playDeckCompleteEffect } from './CardEffects';
import { GameManager, DeckPhase, TierOption } from '../game';
import { PlayerState, CardDef, CardTier, TierColors } from '../types';
import { getCardById } from '../cards';

export interface DeckPhaseCallbacks {
  onComplete: () => void;
}

// グローバル状態
let currentDeckPhase: DeckPhase | null = null;
let currentPlayer: PlayerState | null = null;
let currentGame: GameManager | null = null;
let currentCallbacks: DeckPhaseCallbacks | null = null;

// UI状態
const uiState = {
  phase: 'tierSelect' as 'tierSelect' | 'cardSelect',
  selectedCardIds: new Set<string>(),
  isRedrawMode: false,
  redrawTargetId: null as string | null,
  isRemoveMode: false,
  removeTargetId: null as string | null,
};

export function renderDeckPhase(
  game: GameManager,
  player: PlayerState,
  deckPhase: DeckPhase,
  callbacks: DeckPhaseCallbacks
): void {
  // グローバル参照を保存
  currentDeckPhase = deckPhase;
  currentPlayer = player;
  currentGame = game;
  currentCallbacks = callbacks;

  // 状態リセット
  uiState.phase = deckPhase.hasTierSelected(player.info.id) ? 'cardSelect' : 'tierSelect';
  uiState.selectedCardIds.clear();
  uiState.isRedrawMode = false;
  uiState.redrawTargetId = null;
  uiState.isRemoveMode = false;
  uiState.removeTargetId = null;

  renderUI();
}

function renderUI(): void {
  if (!currentDeckPhase || !currentPlayer || !currentGame) return;

  const gameState = currentGame.getState();
  const round = gameState.currentRound?.roundNumber ?? 1;
  const readyStates = currentGame.getReadyStates();
  const deckCounts = currentDeckPhase.getDeckCounts();
  const availableTiers = currentDeckPhase.getAvailableTiers();
  const minDeckSize = 6;
  const canRemove = currentPlayer.deck.length > minDeckSize;

  render(`
    <div class="deck-phase" style="padding: 12px; max-width: 1100px; margin: 0 auto;">
      ${statusBar(currentPlayer.fans, round, 'デッキフェイズ')}

      <div style="background: rgba(0,0,0,0.3); border-radius: 12px; padding: 12px; margin-bottom: 12px;">
        <div style="display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 8px;">
          <div>
            <span style="color: #ffd700; font-weight: bold;">📦 カード取得</span>
            <span style="color: #aaa; margin-left: 8px;">Tierを選んでカードを獲得</span>
          </div>
          <div style="display: flex; gap: 8px; font-size: 0.8rem;">
            ${(['A', 'B', 'C'] as CardTier[]).map(t => `
              <span style="background: ${TierColors[t]}33; color: ${TierColors[t]}; padding: 2px 8px; border-radius: 4px; border: 1px solid ${TierColors[t]};">
                Tier ${t}: ${deckCounts[t]}枚
              </span>
            `).join('')}
          </div>
        </div>
      </div>

      <div id="readyStatus" style="background: rgba(0,0,0,0.2); border-radius: 8px; padding: 8px 12px; margin-bottom: 12px; display: flex; flex-wrap: wrap; gap: 8px; align-items: center; font-size: 0.8rem;">
        <span style="color: #888;">準備状況:</span>
        ${readyStates.map(rs => `
          <span style="padding: 2px 6px; background: ${rs.isReady ? 'rgba(46,204,113,0.2)' : 'transparent'}; border: 1px solid ${rs.isReady ? '#2ecc71' : '#444'}; border-radius: 12px; color: ${rs.isReady ? '#2ecc71' : '#888'};">
            ${rs.isBot ? '🤖' : '👤'} ${rs.name} ${rs.isReady ? '✓' : ''}
          </span>
        `).join('')}
      </div>

      <div style="display: flex; gap: 16px; flex-wrap: wrap;">
        <div style="flex: 2; min-width: 350px;">
          <div id="mainArea">
            ${uiState.phase === 'tierSelect' ? renderTierSelect(availableTiers, deckCounts) : renderCardSelect()}
          </div>
        </div>

        <div style="flex: 1; min-width: 280px;">
          <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px;">
            <h2 style="color: #00fff5; margin: 0; font-size: 1.1rem;">📦 My Deck</h2>
            <button id="btnToggleRemove" style="background: transparent; border: 1px solid ${canRemove ? '#e74c3c' : '#444'}; color: ${canRemove ? '#e74c3c' : '#666'}; padding: 5px 10px; border-radius: 5px; font-size: 0.8rem; cursor: ${canRemove ? 'pointer' : 'not-allowed'};" ${canRemove ? '' : 'disabled'}>
              ${uiState.isRemoveMode ? '✕ 終了' : '🗑️ 除外'}
            </button>
          </div>
          
          <div id="deckInfo" style="margin-bottom: 10px; color: #aaa; font-size: 0.9rem;"></div>
          
          ${uiState.isRemoveMode ? `
            <div style="background: rgba(231, 76, 60, 0.1); border: 1px solid #e74c3c; border-radius: 8px; padding: 10px; margin-bottom: 10px;">
              <p style="color: #e74c3c; margin: 0 0 8px 0; font-weight: bold;">🗑️ 除外モード</p>
              <p style="color: #aaa; margin: 0; font-size: 0.85rem;">除外するカード1枚をタップ</p>
              <div style="margin-top: 10px; display: flex; gap: 8px;">
                <button id="btnConfirmRemove" style="background: ${uiState.removeTargetId ? '#e74c3c' : '#555'}; border: none; color: #fff; padding: 6px 12px; border-radius: 5px; cursor: ${uiState.removeTargetId ? 'pointer' : 'not-allowed'};" ${uiState.removeTargetId ? '' : 'disabled'}>除外する</button>
                <button id="btnCancelRemove" style="background: transparent; border: 1px solid #666; color: #aaa; padding: 6px 12px; border-radius: 5px; cursor: pointer;">キャンセル</button>
              </div>
              ${uiState.removeTargetId ? `<div style="color: #e74c3c; margin-top: 8px; font-size: 0.85rem;">選択中: ${getCardNameByInstanceId(uiState.removeTargetId)}</div>` : ''}
            </div>
          ` : ''}
          
          <div id="deckCards" style="display: flex; flex-wrap: wrap; gap: 6px;"></div>
        </div>
      </div>

      ${uiState.phase === 'cardSelect' ? `
        <div style="margin-top: 24px; text-align: center;">
          <button id="btnComplete" style="background: linear-gradient(135deg, #00fff5 0%, #00d4aa 100%); border: none; color: #1a1a2e; padding: 14px 40px; border-radius: 8px; font-size: 1.1rem; font-weight: bold; cursor: pointer; box-shadow: 0 0 20px #00fff5;">
            ✅ 準備完了
          </button>
        </div>
      ` : ''}
    </div>
  `);

  renderDeckPanel();
  bindGlobalEvents();
}

function renderTierSelect(tiers: TierOption[], deckCounts: Record<CardTier, number>): string {
  return `
    <h2 style="color: #00fff5; margin: 0 0 16px 0; font-size: 1.1rem;">🎯 Tierを選択</h2>
    <p style="color: #aaa; margin-bottom: 16px; font-size: 0.9rem;">どのTierからカードを引きますか?</p>
    <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 12px;">
      ${tiers.map(opt => `
        <div class="tier-option" data-tier="${opt.tier}" style="background: linear-gradient(135deg, ${TierColors[opt.tier]}22 0%, rgba(0,0,0,0.4) 100%); border: 2px solid ${TierColors[opt.tier]}; border-radius: 12px; padding: 20px; cursor: pointer; transition: all 0.2s; text-align: center;">
          <div style="font-size: 2rem; font-weight: bold; color: ${TierColors[opt.tier]}; margin-bottom: 8px;">Tier ${opt.tier}</div>
          <div style="color: #ffd700; font-size: 1.1rem; margin-bottom: 4px;">${opt.selectCount}枚 選択可能</div>
          <div style="color: #888; font-size: 0.8rem;">山札: ${deckCounts[opt.tier]}枚</div>
        </div>
      `).join('')}
    </div>
  `;
}

function renderCardSelect(): string {
  if (!currentDeckPhase || !currentPlayer) return '';
  
  const state = currentDeckPhase.getPlayerState(currentPlayer.info.id);
  if (!state || !state.selectedTier) return '<p>エラー</p>';

  const tier = state.selectedTier;
  const maxSelect = state.maxSelections;
  const canRedraw = currentDeckPhase.canRedraw(currentPlayer.info.id);

  return `
    <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px;">
      <h2 style="color: #00fff5; margin: 0; font-size: 1.1rem;">🃏 Tier ${tier} から選択</h2>
      <div style="display: flex; gap: 8px; align-items: center;">
        <span id="selectCount" style="color: #00fff5; font-size: 0.9rem;">選択: ${uiState.selectedCardIds.size} / ${maxSelect}</span>
        <button id="btnRedrawMode" style="background: ${canRedraw && !uiState.isRedrawMode ? 'rgba(241, 196, 15, 0.2)' : '#333'}; border: 1px solid ${canRedraw && !uiState.isRedrawMode ? '#f1c40f' : '#444'}; color: ${canRedraw && !uiState.isRedrawMode ? '#f1c40f' : '#666'}; padding: 4px 10px; border-radius: 4px; font-size: 0.75rem; cursor: ${canRedraw && !uiState.isRedrawMode ? 'pointer' : 'not-allowed'};" ${canRedraw && !uiState.isRedrawMode ? '' : 'disabled'}>
          🔄 引き直し
        </button>
      </div>
    </div>

    ${uiState.isRedrawMode ? `
      <div style="background: rgba(241, 196, 15, 0.1); border: 1px solid #f1c40f; border-radius: 8px; padding: 10px; margin-bottom: 12px;">
        <p style="color: #f1c40f; margin: 0 0 6px 0; font-size: 0.85rem; font-weight: bold;">🔄 引き直したいカードを1枚選択</p>
        <p style="color: #888; margin: 0; font-size: 0.75rem;">※選択済みのカードは引き直しできません</p>
        <div style="margin-top: 8px; display: flex; gap: 8px;">
          <button id="btnConfirmRedraw" style="background: ${uiState.redrawTargetId ? '#f1c40f' : '#555'}; border: none; color: #000; padding: 4px 10px; border-radius: 4px; cursor: ${uiState.redrawTargetId ? 'pointer' : 'not-allowed'}; font-size: 0.75rem;" ${uiState.redrawTargetId ? '' : 'disabled'}>引き直す</button>
          <button id="btnCancelRedraw" style="background: transparent; border: 1px solid #666; color: #aaa; padding: 4px 10px; border-radius: 4px; cursor: pointer; font-size: 0.75rem;">キャンセル</button>
        </div>
        ${uiState.redrawTargetId ? `<div style="color: #f1c40f; margin-top: 6px; font-size: 0.8rem;">選択中: ${getCardNameByKey(uiState.redrawTargetId)}</div>` : ''}
      </div>
    ` : ''}

    <div id="drawnCards" style="display: grid; grid-template-columns: repeat(auto-fill, minmax(100px, 1fr)); gap: 10px;">
      ${state.drawnCards.map((card, idx) => renderDrawnCard(card, idx)).join('')}
    </div>
  `;
}

function renderDrawnCard(card: CardDef, idx: number): string {
  const cardKey = `idx-${idx}`;
  const isSelected = uiState.selectedCardIds.has(cardKey);
  const isRedrawTarget = uiState.redrawTargetId === cardKey;
  const hasAbility = card.abilities.length > 0;
  const canRedrawThis = uiState.isRedrawMode && !uiState.selectedCardIds.has(cardKey);

  let borderStyle = 'border: 2px solid #333;';
  let extraStyle = '';
  
  if (isRedrawTarget) {
    borderStyle = 'border: 3px solid #f1c40f;';
    extraStyle = 'box-shadow: 0 0 15px rgba(241, 196, 15, 0.5);';
  } else if (isSelected) {
    borderStyle = 'border: 3px solid #00fff5;';
    extraStyle = 'box-shadow: 0 0 15px rgba(0, 255, 245, 0.5);';
  } else if (uiState.isRedrawMode && !canRedrawThis) {
    extraStyle = 'opacity: 0.4;';
  }

  return `
    <div class="drawn-card" data-card-idx="${idx}" style="${borderStyle} ${extraStyle} border-radius: 10px; padding: 10px; background: linear-gradient(180deg, ${getCategoryColor(card.category)}33 0%, rgba(0,0,0,0.8) 100%); cursor: pointer; transition: all 0.2s; position: relative;">
      ${isSelected ? '<div style="position: absolute; top: -8px; right: -8px; background: #00fff5; color: #000; width: 22px; height: 22px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-weight: bold; font-size: 0.75rem; z-index: 10;">✓</div>' : ''}
      ${isRedrawTarget ? '<div style="position: absolute; top: -8px; right: -8px; background: #f1c40f; color: #000; width: 22px; height: 22px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 0.7rem; z-index: 10;">🔄</div>' : ''}
      ${hasAbility ? '<div style="position: absolute; top: 4px; right: 4px; background: #9b59b6; color: #fff; padding: 1px 5px; border-radius: 3px; font-size: 0.6rem;">✨</div>' : ''}
      <div style="text-align: center;">
        <div style="font-size: 1.4rem; margin-bottom: 4px;">${getCategoryIcon(card.category)}</div>
        <div style="color: #fff; font-weight: bold; font-size: 0.8rem; margin-bottom: 4px;">${card.name}</div>
        <div style="background: rgba(255, 215, 0, 0.2); color: #ffd700; padding: 3px 8px; border-radius: 4px; font-size: 0.95rem; font-weight: bold;">⚡${card.basePower}</div>
      </div>
      ${hasAbility ? `<div style="margin-top: 8px; padding-top: 6px; border-top: 1px solid rgba(255,255,255,0.1); font-size: 0.65rem; color: #aaa; text-align: center;">${card.abilities[0].effect.description}</div>` : ''}
    </div>
  `;
}

function renderDeckPanel(): void {
  if (!currentPlayer) return;

  const infoEl = document.getElementById('deckInfo');
  const cardsEl = document.getElementById('deckCards');
  if (!infoEl || !cardsEl) return;

  const catCount: Record<string, number> = {};
  const tierCount: Record<CardTier, number> = { A: 0, B: 0, C: 0 };
  let totalPower = 0;

  for (const card of currentPlayer.deck) {
    const def = getCardById(card.defId);
    if (def) {
      catCount[def.category] = (catCount[def.category] ?? 0) + 1;
      tierCount[def.tier]++;
      totalPower += card.currentPower;
    }
  }

  const avgPower = currentPlayer.deck.length > 0 ? (totalPower / currentPlayer.deck.length).toFixed(1) : '0';

  infoEl.innerHTML = `
    <div style="display: flex; gap: 10px; flex-wrap: wrap; margin-bottom: 4px;">
      <span>📚 ${currentPlayer.deck.length}枚</span>
      <span>⚡ 平均${avgPower}</span>
    </div>
    <div style="display: flex; gap: 6px; flex-wrap: wrap; font-size: 0.8rem;">
      ${(['A', 'B', 'C'] as CardTier[]).map(t => `<span style="color: ${TierColors[t]};">${t}:${tierCount[t]}</span>`).join('')}
      |
      ${Object.entries(catCount).map(([cat, count]) => `<span>${getCategoryIcon(cat)}${count}</span>`).join('')}
    </div>
  `;

  cardsEl.innerHTML = currentPlayer.deck.map(card => {
    const def = getCardById(card.defId);
    if (!def) return '';
    
    const isRemoveTarget = uiState.removeTargetId === card.instanceId;
    const hasAbility = def.abilities.length > 0;
    
    return `
      <div class="deck-card" data-instance="${card.instanceId}" style="cursor: pointer; position: relative; ${isRemoveTarget ? 'border: 2px solid #e74c3c; box-shadow: 0 0 10px rgba(231, 76, 60, 0.5);' : ''}">
        ${isRemoveTarget ? '<div style="position: absolute; top: -5px; right: -5px; background: #e74c3c; color: #fff; width: 16px; height: 16px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 0.6rem; z-index: 10;">✕</div>' : ''}
        <div style="position: absolute; top: 2px; left: 2px; background: ${TierColors[def.tier]}; color: #fff; padding: 1px 3px; border-radius: 2px; font-size: 0.55rem; font-weight: bold; z-index: 5;">${def.tier}</div>
        ${hasAbility ? '<div style="position: absolute; top: 2px; right: 2px; font-size: 0.6rem;">✨</div>' : ''}
        ${cardDisplay(def.name, card.currentPower, def.category, def.rarity, [])}
      </div>
    `;
  }).join('');
}

function bindGlobalEvents(): void {
  // Tier選択
  document.querySelectorAll('.tier-option').forEach(el => {
    el.addEventListener('click', handleTierSelect);
  });

  // カード選択
  document.querySelectorAll('.drawn-card').forEach(el => {
    el.addEventListener('click', handleCardClick);
  });

  // デッキカード
  document.querySelectorAll('.deck-card').forEach(el => {
    el.addEventListener('click', handleDeckCardClick);
  });

  // ボタン類
  document.getElementById('btnRedrawMode')?.addEventListener('click', () => {
    if (currentDeckPhase?.canRedraw(currentPlayer?.info.id ?? '')) {
      uiState.isRedrawMode = true;
      uiState.redrawTargetId = null;
      renderUI();
    }
  });

  document.getElementById('btnConfirmRedraw')?.addEventListener('click', handleConfirmRedraw);
  document.getElementById('btnCancelRedraw')?.addEventListener('click', () => {
    uiState.isRedrawMode = false;
    uiState.redrawTargetId = null;
    renderUI();
  });

  document.getElementById('btnToggleRemove')?.addEventListener('click', () => {
    uiState.isRemoveMode = !uiState.isRemoveMode;
    uiState.removeTargetId = null;
    renderUI();
  });

  document.getElementById('btnConfirmRemove')?.addEventListener('click', handleConfirmRemove);
  document.getElementById('btnCancelRemove')?.addEventListener('click', () => {
    uiState.isRemoveMode = false;
    uiState.removeTargetId = null;
    renderUI();
  });

  document.getElementById('btnComplete')?.addEventListener('click', handleComplete);
}

function handleTierSelect(e: Event): void {
  const el = e.currentTarget as HTMLElement;
  const tier = el.getAttribute('data-tier') as CardTier;
  if (!tier || !currentDeckPhase || !currentPlayer) return;

  const cards = currentDeckPhase.selectTier(currentPlayer.info.id, tier);
  if (cards) {
    uiState.phase = 'cardSelect';
    renderUI();
  }
}

function handleCardClick(e: Event): void {
  const el = e.currentTarget as HTMLElement;
  const cardIdx = el.getAttribute('data-card-idx');
  if (cardIdx === null || !currentDeckPhase || !currentPlayer) return;

  const cardKey = `idx-${cardIdx}`;
  const state = currentDeckPhase.getPlayerState(currentPlayer.info.id);
  if (!state) return;

  if (uiState.isRedrawMode) {
    // 引き直しモード: 選択済みカードは不可
    if (uiState.selectedCardIds.has(cardKey)) {
      showToast('選択済みカードは引き直し不可', '#e74c3c');
      return;
    }
    uiState.redrawTargetId = cardKey;
    renderUI();
  } else {
    // 通常選択モード
    if (uiState.selectedCardIds.has(cardKey)) {
      uiState.selectedCardIds.delete(cardKey);
    } else if (uiState.selectedCardIds.size < state.maxSelections) {
      uiState.selectedCardIds.add(cardKey);
    } else {
      showToast(`最大${state.maxSelections}枚まで`, '#e74c3c');
      return;
    }
    renderUI();
  }
}

function handleDeckCardClick(e: Event): void {
  const el = e.currentTarget as HTMLElement;
  const instanceId = el.getAttribute('data-instance');
  if (!instanceId || !currentPlayer) return;

  if (uiState.isRemoveMode) {
    // 1枚だけ選択
    uiState.removeTargetId = uiState.removeTargetId === instanceId ? null : instanceId;
    renderUI();
  } else {
    const card = currentPlayer.deck.find(c => c.instanceId === instanceId);
    if (card) showCardDetail(card);
  }
}

function handleConfirmRedraw(): void {
  if (!uiState.redrawTargetId || !currentDeckPhase || !currentPlayer) return;

  // idx-N形式からインデックスを取り出してカードIDを取得
  const idxMatch = uiState.redrawTargetId.match(/^idx-(\d+)$/);
  if (!idxMatch) return;
  
  const idx = parseInt(idxMatch[1], 10);
  const state = currentDeckPhase.getPlayerState(currentPlayer.info.id);
  if (!state || !state.drawnCards[idx]) return;
  
  const cardId = state.drawnCards[idx].id;
  const newCard = currentDeckPhase.redrawCard(currentPlayer.info.id, cardId);
  if (newCard) {
    showToast('1枚引き直し', '#f1c40f');
    uiState.isRedrawMode = false;
    uiState.redrawTargetId = null;
    renderUI();
  }
}

function handleConfirmRemove(): void {
  if (!uiState.removeTargetId || !currentDeckPhase || !currentPlayer) return;

  const success = currentDeckPhase.removeCard(currentPlayer, uiState.removeTargetId);
  if (success) {
    showToast('1枚除外', '#e74c3c');
    uiState.isRemoveMode = false;
    uiState.removeTargetId = null;
    renderUI();
  }
}

async function handleComplete(): Promise<void> {
  if (!currentDeckPhase || !currentPlayer || !currentGame || !currentCallbacks) return;

  const btn = document.getElementById('btnComplete') as HTMLButtonElement;
  if (btn) {
    btn.disabled = true;
    btn.textContent = '準備中...';
  }

  // インデックスから実際のカードIDを解決
  const state = currentDeckPhase.getPlayerState(currentPlayer.info.id);
  if (!state) return;

  const selectedIds: string[] = [];
  for (const key of uiState.selectedCardIds) {
    const match = key.match(/^idx-(\d+)$/);
    if (match) {
      const idx = parseInt(match[1], 10);
      if (state.drawnCards[idx]) {
        selectedIds.push(state.drawnCards[idx].id);
      }
    }
  }

  // 選択したカードをデッキに追加
  currentDeckPhase.addSelectedCards(currentPlayer, selectedIds);

  const deckArea = document.getElementById('deckCards');
  if (deckArea) {
    await playDeckCompleteEffect(deckArea, currentPlayer.deck.length);
  }

  currentGame.setPlayerReady(currentPlayer.info.id, true);
  
  if (btn) btn.textContent = '待機中...';
  
  currentCallbacks.onComplete();
}

// ヘルパー関数
function getCardNameByKey(cardKey: string): string {
  if (!currentDeckPhase || !currentPlayer) return '';
  const state = currentDeckPhase.getPlayerState(currentPlayer.info.id);
  if (!state) return '';
  
  const match = cardKey.match(/^idx-(\d+)$/);
  if (match) {
    const idx = parseInt(match[1], 10);
    return state.drawnCards[idx]?.name ?? '';
  }
  return '';
}

function getCardNameByInstanceId(instanceId: string): string {
  if (!currentPlayer) return '';
  const card = currentPlayer.deck.find(c => c.instanceId === instanceId);
  if (!card) return '';
  const def = getCardById(card.defId);
  return def?.name ?? '';
}

function showToast(message: string, color: string = '#00fff5'): void {
  const existing = document.querySelector('.deck-toast');
  if (existing) existing.remove();

  const toast = document.createElement('div');
  toast.className = 'deck-toast';
  toast.innerHTML = `<div style="position: fixed; top: 15%; left: 50%; transform: translateX(-50%); background: ${color}; color: #fff; padding: 10px 20px; border-radius: 8px; font-weight: bold; z-index: 2000; animation: toastAnim 1.5s ease-in-out forwards;">${message}</div>`;
  document.body.appendChild(toast);
  setTimeout(() => toast.remove(), 1500);
}

function getCategoryIcon(category: string): string {
  const icons: Record<string, string> = { 
    warrior: '⚔️', thief: '🗡️', mage: '🔮', machine: '⚙️', undead: '💀',
    dragon: '🐉', spirit: '✨', beast: '🐺', angel: '👼', demon: '😈', golem: '🗿',
    castle: '🏰', city: '🏙️', space: '🚀', movie: '🎬', ship: '🚢', ghost: '👻', park: '🎡'
  };
  return icons[category] ?? '?';
}

function getCategoryColor(category: string): string {
  const colors: Record<string, string> = {
    warrior: '#e74c3c', thief: '#9b59b6', mage: '#3498db', machine: '#95a5a6', undead: '#1abc9c',
    dragon: '#e67e22', spirit: '#00bcd4', beast: '#8d6e63', angel: '#ffd700', demon: '#8e44ad', golem: '#607d8b',
    castle: '#8b4513', city: '#4682b4', space: '#483d8b', movie: '#dc143c', ship: '#20b2aa', ghost: '#4b0082', park: '#ff69b4'
  };
  return colors[category] ?? '#666';
}

// 準備状況更新
export function updateReadyStatus(game: GameManager): void {
  const readyStates = game.getReadyStates();
  const container = document.getElementById('readyStatus');
  if (!container) return;

  container.innerHTML = `
    <span style="color: #888;">準備状況:</span>
    ${readyStates.map(rs => `
      <span style="padding: 3px 8px; background: ${rs.isReady ? 'rgba(46,204,113,0.2)' : 'transparent'}; border: 1px solid ${rs.isReady ? '#2ecc71' : '#444'}; border-radius: 12px; color: ${rs.isReady ? '#2ecc71' : '#888'};">
        ${rs.isBot ? '🤖' : '👤'} ${rs.name} ${rs.isReady ? '✓' : ''}
      </span>
    `).join('')}
  `;
}

// CSS
const style = document.createElement('style');
style.textContent = `
  @keyframes toastAnim {
    0% { opacity: 0; transform: translateX(-50%) translateY(-10px); }
    15% { opacity: 1; transform: translateX(-50%) translateY(0); }
    85% { opacity: 1; }
    100% { opacity: 0; }
  }
  .tier-option:hover { transform: scale(1.02); box-shadow: 0 0 20px rgba(255, 255, 255, 0.1); }
  .drawn-card:hover { transform: scale(1.03); }
  .deck-card:hover { transform: scale(1.05); }
`;
if (!document.querySelector('style[data-deck-phase]')) {
  style.setAttribute('data-deck-phase', 'true');
  document.head.appendChild(style);
}
